/**
 * Local Storage Service
 * Manages persistent storage using localStorage for authentication tokens, session IDs, and bot IDs
 *
 * Features:
 * - Stores auth token with 24-hour expiry
 * - Stores session data (sessionID and botID) with 7-day expiry
 * - Stores user preference for last selected bot with 30-day expiry
 * - Automatic expiration handling with manual checks
 * - Simpler API than cookies, works within same-origin contexts
 */

interface StoredAuthData {
  token: string;
  userId: string;
  userGroups: string[];
  userName?: string;
  userEmail?: string;
  givenName?: string;
  surname?: string;
  expiresAt: number; // Unix timestamp
}

interface StoredSessionData {
  sessionId: string;
  botId: string;
  timestamp: number; // Unix timestamp
  expiresAt: number; // Unix timestamp
}

interface StoredBotIdData {
  botId: string;
  expiresAt: number; // Unix timestamp
}

// For iframe mode with state param: only store botId (state IS the session identifier)
interface StoredStateBotData {
  botId: string;
  timestamp: number; // Unix timestamp
  expiresAt: number; // Unix timestamp
}

interface ClientStateBotMap {
  [state: string]: StoredStateBotData; // Map of client state -> bot preference only
}

// Brand preference storage - isolated from session lifecycle
interface StoredBrandData {
  brand: string;
  timestamp: number; // Unix timestamp
  expiresAt: number; // Unix timestamp
}

interface SessionBrandMap {
  [sessionId: string]: StoredBrandData; // Map of sessionId -> brand preference
}

// Deal preference storage for Finance Cleve - isolated from session lifecycle
interface StoredDealData {
  deal_id: string;
  ke_table_name: string;
  deal_name: string;
  timestamp: number; // Unix timestamp
  expiresAt: number; // Unix timestamp
}

interface SessionDealMap {
  [sessionId: string]: StoredDealData; // Map of sessionId -> deal preference
}

interface StoredCurrentAgentData {
  agentValue: string; // The dropdown value (e.g., "customAzure")
  agentName: string;  // The agent display name (e.g., "Azure")
  expiresAt: number;  // Unix timestamp
}

// Pre-validation tracking for NiceCxOne iframe sessions
interface PreValidationData {
  state: string;
  completedAt: number;  // timestamp when pre-validation was sent
  messageContent: string;  // the message that was sent (for debugging)
  expiresAt: number;
}

interface PreValidationStateMap {
  [state: string]: PreValidationData;
}

const STORAGE_KEYS = {
  AUTH_DATA: 'nrg_chatbot_user_data',  // Renamed from auth_data to avoid browser security filters
  SESSION_DATA: 'nrg_chatbot_session_data',  // Normal mode: stores sessionId + botId for session restoration
  CLIENT_STATE_BOTS: 'nrg_chatbot_client_state_bots',  // Iframe mode: stores only botId per state (state IS sessionId)
  SESSION_BRANDS: 'nrg_chatbot_session_brands',  // Brand preferences mapped by sessionId (decoupled from session lifecycle)
  SESSION_DEALS: 'nrg_chatbot_session_deals',  // Deal preferences mapped by sessionId (Finance Cleve)
  LAST_BOT_ID: 'nrg_chatbot_last_bot_id',
  CURRENT_AGENT: 'nrg_chatbot_current_agent',  // Current dropdown selection for session persistence
  PREVALIDATION_STATES: 'nrg_chatbot_prevalidation_states',  // Tracks which states have completed pre-validation
} as const;

const TOKEN_EXPIRY_HOURS = 24;
const SESSION_EXPIRY_HOURS = 24 * 7; // 7 days
const BOT_PREFERENCE_EXPIRY_HOURS = 24 * 30; // 30 days

class LocalStorageService {
  private static instance: LocalStorageService;

  private constructor() {
    if (LocalStorageService.instance) {
      return LocalStorageService.instance;
    }
    LocalStorageService.instance = this;
  }

  public static getInstance(): LocalStorageService {
    if (!LocalStorageService.instance) {
      LocalStorageService.instance = new LocalStorageService();
    }
    return LocalStorageService.instance;
  }

  /**
   * Set an item in localStorage with JSON serialization
   */
  private setItem(key: string, value: any): void {
    try {
      console.log(`[localStorageService] setItem() called for key: ${key}`);
      const jsonString = JSON.stringify(value);
      console.log(`[localStorageService] Serialized value length:`, jsonString.length, 'characters');
      console.log(`[localStorageService] Serialized value (truncated):`, jsonString.substring(0, 100) + '...');

      // Check localStorage quota
      let totalSize = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          totalSize += key.length + (localStorage.getItem(key)?.length || 0);
        }
      }
      console.log(`[localStorageService] Current localStorage usage: ${(totalSize / 1024).toFixed(2)} KB`);
      console.log(`[localStorageService] New item size: ${(jsonString.length / 1024).toFixed(2)} KB`);

      localStorage.setItem(key, jsonString);

      // Verify it was actually saved
      const verification = localStorage.getItem(key);
      console.log(`[localStorageService] Verification - item saved: ${verification !== null}`);
      console.log(`[localStorageService] Verification - length matches: ${verification?.length === jsonString.length}`);

      if (verification !== jsonString) {
        console.error(`[localStorageService] ⚠️ WARNING: Saved value doesn't match!`);
        console.error(`[localStorageService] Expected length: ${jsonString.length}, Got: ${verification?.length || 0}`);
      } else {
        console.log(`[localStorageService] ✅ Item successfully saved and verified`);
      }

      // Check again after 100ms to see if it persists
      setTimeout(() => {
        const delayedCheck = localStorage.getItem(key);
        if (delayedCheck === null) {
          console.error(`[localStorageService] ⚠️ CRITICAL: Item ${key} was deleted within 100ms!`);
        } else if (delayedCheck !== jsonString) {
          console.error(`[localStorageService] ⚠️ CRITICAL: Item ${key} was modified within 100ms!`);
        } else {
          console.log(`[localStorageService] ✅ Item ${key} still intact after 100ms`);
        }
      }, 100);

    } catch (error) {
      console.error(`[localStorageService] Error setting localStorage item ${key}:`, error);
      if (error instanceof Error) {
        console.error(`[localStorageService] Error name: ${error.name}`);
        console.error(`[localStorageService] Error message: ${error.message}`);
      }
    }
  }

  /**
   * Get an item from localStorage with JSON parsing
   */
  private getItem<T>(key: string): T | null {
    try {
      console.log(`[localStorageService] getItem() called for key: ${key}`);
      const item = localStorage.getItem(key);
      console.log(`[localStorageService] Raw item from localStorage:`, item);

      if (!item) {
        console.log(`[localStorageService] Item is null/undefined for key: ${key}`);
        return null;
      }

      console.log(`[localStorageService] Attempting to parse JSON...`);
      const parsed = JSON.parse(item) as T;
      console.log(`[localStorageService] Successfully parsed:`, parsed);
      return parsed;
    } catch (error) {
      console.error(`[localStorageService] Error getting/parsing localStorage item ${key}:`, error);
      console.error(`[localStorageService] Raw value that failed to parse:`, localStorage.getItem(key));
      return null;
    }
  }

  /**
   * Remove an item from localStorage
   */
  private removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage item ${key}:`, error);
    }
  }

  /**
   * Save authentication data with 24-hour expiry
   */
  public saveAuthData(token: string, userId: string, userGroups: string[], userName?: string, userEmail?: string, givenName?: string, surname?: string): void {
    try {
      const expiresAt = Date.now() + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000);
      const authData: StoredAuthData = {
        token,
        userId,
        userGroups,
        userName,
        userEmail,
        givenName,
        surname,
        expiresAt,
      };
      console.log('[localStorageService] saveAuthData() called');
      console.log('[localStorageService] Saving auth data - expiresAt:', expiresAt);
      console.log('[localStorageService] Current time:', Date.now());
      console.log('[localStorageService] Expiry in hours:', TOKEN_EXPIRY_HOURS);
      this.setItem(STORAGE_KEYS.AUTH_DATA, authData);
      console.log('[localStorageService] ✅ Auth data saved to localStorage with 24hr expiry');
    } catch (error) {
      console.error('[localStorageService] Error saving auth data to localStorage:', error);
    }
  }

  /**
   * Retrieve authentication data if not expired
   */
  public getAuthData(): StoredAuthData | null {
    try {
      console.log('[localStorageService] getAuthData() called');
      const authData = this.getItem<StoredAuthData>(STORAGE_KEYS.AUTH_DATA);

      if (!authData) {
        console.log('[localStorageService] No auth data found in localStorage');
        return null;
      }

      console.log('[localStorageService] Auth data found, checking expiry...');
      console.log('[localStorageService] Current time:', Date.now());
      console.log('[localStorageService] Token expiresAt:', authData.expiresAt);
      console.log('[localStorageService] Time remaining (ms):', authData.expiresAt - Date.now());

      // Check if token has expired
      if (Date.now() > authData.expiresAt) {
        console.log('[localStorageService] ❌ Auth token expired, clearing localStorage');
        this.clearAuthData();
        return null;
      }

      console.log('[localStorageService] ✅ Auth token valid, returning data');
      return authData;
    } catch (error) {
      console.error('[localStorageService] Error retrieving auth data from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear authentication data
   */
  public clearAuthData(): void {
    try {
      console.log('[localStorageService] clearAuthData() called');
      console.trace('[localStorageService] Stack trace for clearAuthData:');
      this.removeItem(STORAGE_KEYS.AUTH_DATA);
      console.log('[localStorageService] Auth data cleared from localStorage');
    } catch (error) {
      console.error('[localStorageService] Error clearing auth data from localStorage:', error);
    }
  }

  /**
   * Save session ID (conversation ID) with botID
   * Using 7 days expiry for session data
   * @param sessionId - The conversation/session ID
   * @param botId - The bot/agent ID
   * @param state - Optional client state ID for multi-client session management
   *
   * IMPORTANT: When state is provided, ONLY botId is saved (state IS the session identifier).
   * Other states' data remains untouched.
   */
  public saveSessionData(sessionId: string, botId: string, state?: string): void {
    try {
      const expiresAt = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000);

      if (state) {
        // Iframe mode: Save ONLY botId preference (state parameter IS the session identifier)
        // The actual sessionId is constructed as NICECXONE_{state} by the backend
        const stateBotData: StoredStateBotData = {
          botId,
          timestamp: Date.now(),
          expiresAt,
        };
        const clientStateBots = this.getItem<ClientStateBotMap>(STORAGE_KEYS.CLIENT_STATE_BOTS) || {};
        clientStateBots[state] = stateBotData;
        this.setItem(STORAGE_KEYS.CLIENT_STATE_BOTS, clientStateBots);
        console.log(`[localStorageService] ✅ Bot preference saved for state "${state}":`, { botId });
        console.log(`[localStorageService] Session ID is derived from state: NICECXONE_${state}`);
      } else {
        // Legacy mode: save to single session storage (backward compatibility)
        const sessionData: StoredSessionData = {
          sessionId,
          botId,
          timestamp: Date.now(),
          expiresAt,
        };
        this.setItem(STORAGE_KEYS.SESSION_DATA, sessionData);
        console.log('[localStorageService] Session data saved to legacy storage:', { sessionId, botId });
      }
    } catch (error) {
      console.error('[localStorageService] Error saving session data to localStorage:', error);
    }
  }

  /**
   * Retrieve session data if not expired
   * @param state - Optional client state ID for multi-client session management
   *
   * IMPORTANT: When state is provided, this returns botId only (sessionId is derived from state).
   * No other states' data is accessed or affected.
   */
  public getSessionData(state?: string): StoredSessionData | null {
    try {
      if (state) {
        // Iframe mode: Retrieve ONLY botId (sessionId is NICECXONE_{state})
        const clientStateBots = this.getItem<ClientStateBotMap>(STORAGE_KEYS.CLIENT_STATE_BOTS);
        const stateBotData = clientStateBots?.[state] || null;

        if (!stateBotData) {
          console.log(`[localStorageService] No bot preference found for state "${state}"`);
          return null;
        }

        // Check if data has expired
        if (Date.now() > stateBotData.expiresAt) {
          console.log(`[localStorageService] Bot preference expired for state "${state}"`);
          this.clearSessionData(state);
          return null;
        }

        console.log(`[localStorageService] ✅ Bot preference retrieved for state "${state}":`, stateBotData.botId);

        // Return in StoredSessionData format for compatibility
        // SessionId is constructed from state (NICECXONE_{state})
        return {
          sessionId: `NICECXONE_${state}`,
          botId: stateBotData.botId,
          timestamp: stateBotData.timestamp,
          expiresAt: stateBotData.expiresAt,
        };
      } else {
        // Legacy mode: retrieve from single session storage (backward compatibility)
        const sessionData = this.getItem<StoredSessionData>(STORAGE_KEYS.SESSION_DATA);

        if (!sessionData) {
          console.log('[localStorageService] No session data found in legacy storage');
          return null;
        }

        // Check if session has expired
        if (Date.now() > sessionData.expiresAt) {
          console.log('[localStorageService] Session data expired, clearing');
          this.clearSessionData();
          return null;
        }

        console.log('[localStorageService] Session data retrieved from legacy storage');
        return sessionData;
      }
    } catch (error) {
      console.error('[localStorageService] Error retrieving session data from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear session data
   * @param state - Optional client state ID for multi-client session management
   *
   * IMPORTANT: When state is provided, this ONLY clears that specific state's bot preference.
   * All other states' data remains intact and untouched.
   */
  public clearSessionData(state?: string): void {
    try {
      if (state) {
        // Iframe mode: remove ONLY this specific state's bot preference from map
        // All other states remain untouched
        const clientStateBots = this.getItem<ClientStateBotMap>(STORAGE_KEYS.CLIENT_STATE_BOTS);
        if (clientStateBots && clientStateBots[state]) {
          delete clientStateBots[state];
          this.setItem(STORAGE_KEYS.CLIENT_STATE_BOTS, clientStateBots);
          console.log(`[localStorageService] ✅ Bot preference cleared for state "${state}"`);
          console.log(`[localStorageService] Other states remain intact:`, Object.keys(clientStateBots).filter(k => k !== state));
        } else {
          console.log(`[localStorageService] No bot preference found to clear for state "${state}"`);
        }
      } else {
        // Legacy mode: clear single session storage (backward compatibility)
        this.removeItem(STORAGE_KEYS.SESSION_DATA);
        console.log('[localStorageService] Session data cleared from legacy storage');
      }
    } catch (error) {
      console.error('[localStorageService] Error clearing session data from localStorage:', error);
    }
  }

  /**
   * Save the last selected bot ID for user preference
   * Using 30 days expiry for preference
   */
  public saveLastBotId(botId: string): void {
    try {
      const expiresAt = Date.now() + (BOT_PREFERENCE_EXPIRY_HOURS * 60 * 60 * 1000);
      const botIdData: StoredBotIdData = {
        botId,
        expiresAt,
      };
      this.setItem(STORAGE_KEYS.LAST_BOT_ID, botIdData);
      console.log('Last bot ID saved to localStorage:', botId);
    } catch (error) {
      console.error('Error saving last bot ID to localStorage:', error);
    }
  }

  /**
   * Retrieve the last selected bot ID if not expired
   */
  public getLastBotId(): string | null {
    try {
      const botIdData = this.getItem<StoredBotIdData>(STORAGE_KEYS.LAST_BOT_ID);
      if (!botIdData) {
        return null;
      }

      // Check if bot preference has expired
      if (Date.now() > botIdData.expiresAt) {
        console.log('Bot preference expired, clearing localStorage');
        this.clearLastBotId();
        return null;
      }

      return botIdData.botId;
    } catch (error) {
      console.error('Error retrieving last bot ID from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear the last selected bot ID
   */
  public clearLastBotId(): void {
    try {
      this.removeItem(STORAGE_KEYS.LAST_BOT_ID);
      console.log('Last bot ID cleared from localStorage');
    } catch (error) {
      console.error('Error clearing last bot ID from localStorage:', error);
    }
  }

  /**
   * Get all client state bot preferences (for debugging/management)
   */
  public getAllClientStateBots(): ClientStateBotMap {
    try {
      return this.getItem<ClientStateBotMap>(STORAGE_KEYS.CLIENT_STATE_BOTS) || {};
    } catch (error) {
      console.error('[localStorageService] Error retrieving all client state bots:', error);
      return {};
    }
  }

  /**
   * Clear all client state bot preferences
   */
  public clearAllClientStateBots(): void {
    try {
      this.removeItem(STORAGE_KEYS.CLIENT_STATE_BOTS);
      console.log('[localStorageService] All client state bot preferences cleared');
    } catch (error) {
      console.error('[localStorageService] Error clearing all client state bots:', error);
    }
  }

  /**
   * Save current agent dropdown selection with session expiry
   * This persists the current dropdown state across page refreshes
   */
  public saveCurrentAgent(agentValue: string, agentName: string): void {
    try {
      const expiresAt = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000); // Same expiry as session
      const currentAgentData: StoredCurrentAgentData = {
        agentValue,
        agentName,
        expiresAt,
      };
      this.setItem(STORAGE_KEYS.CURRENT_AGENT, currentAgentData);
    } catch (error) {
      console.error('[localStorageService] Error saving current agent:', error);
    }
  }

  /**
   * Retrieve current agent dropdown selection if not expired
   */
  public getCurrentAgent(): StoredCurrentAgentData | null {
    try {
      const currentAgentData = this.getItem<StoredCurrentAgentData>(STORAGE_KEYS.CURRENT_AGENT);

      if (!currentAgentData) {
        return null;
      }

      // Check if current agent data has expired
      if (Date.now() > currentAgentData.expiresAt) {
        this.clearCurrentAgent();
        return null;
      }

      return currentAgentData;
    } catch (error) {
      console.error('[localStorageService] Error retrieving current agent:', error);
      return null;
    }
  }

  /**
   * Clear current agent dropdown selection
   */
  public clearCurrentAgent(): void {
    try {
      this.removeItem(STORAGE_KEYS.CURRENT_AGENT);
    } catch (error) {
      console.error('[localStorageService] Error clearing current agent:', error);
    }
  }

  /**
   * Save brand preference for a specific session
   * Isolated storage - does NOT affect session lifecycle
   * @param sessionId - The session/conversation ID
   * @param brand - The brand to save (e.g., 'RELIANT', 'Green Mountain Energy')
   */
  public saveBrandForSession(sessionId: string, brand: string): void {
    try {
      const expiresAt = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000); // Same expiry as session
      const brandData: StoredBrandData = {
        brand,
        timestamp: Date.now(),
        expiresAt,
      };

      const sessionBrands = this.getItem<SessionBrandMap>(STORAGE_KEYS.SESSION_BRANDS) || {};
      sessionBrands[sessionId] = brandData;
      this.setItem(STORAGE_KEYS.SESSION_BRANDS, sessionBrands);
      console.log(`[localStorageService] ✅ Brand saved for session "${sessionId}":`, brand);
    } catch (error) {
      console.error('[localStorageService] Error saving brand for session:', error);
    }
  }

  /**
   * Get brand preference for a specific session
   * @param sessionId - The session/conversation ID
   * @returns The brand string or null if not found/expired
   */
  public getBrandForSession(sessionId: string): string | null {
    try {
      const sessionBrands = this.getItem<SessionBrandMap>(STORAGE_KEYS.SESSION_BRANDS);
      const brandData = sessionBrands?.[sessionId];

      if (!brandData) {
        console.log(`[localStorageService] No brand found for session "${sessionId}"`);
        return null;
      }

      // Check if brand data has expired
      if (Date.now() > brandData.expiresAt) {
        console.log(`[localStorageService] Brand expired for session "${sessionId}", clearing`);
        this.clearBrandForSession(sessionId);
        return null;
      }

      console.log(`[localStorageService] ✅ Brand retrieved for session "${sessionId}":`, brandData.brand);
      return brandData.brand;
    } catch (error) {
      console.error('[localStorageService] Error getting brand for session:', error);
      return null;
    }
  }

  /**
   * Clear brand preference for a specific session
   * @param sessionId - The session/conversation ID
   */
  public clearBrandForSession(sessionId: string): void {
    try {
      const sessionBrands = this.getItem<SessionBrandMap>(STORAGE_KEYS.SESSION_BRANDS);

      if (sessionBrands && sessionBrands[sessionId]) {
        delete sessionBrands[sessionId];
        this.setItem(STORAGE_KEYS.SESSION_BRANDS, sessionBrands);
        console.log(`[localStorageService] ✅ Brand cleared for session "${sessionId}"`);
      } else {
        console.log(`[localStorageService] No brand found to clear for session "${sessionId}"`);
      }
    } catch (error) {
      console.error('[localStorageService] Error clearing brand for session:', error);
    }
  }

  /**
   * Save deal preference for a specific session (Finance Cleve)
   * @param sessionId - The session/conversation ID
   * @param deal - The selected deal (deal_id, ke_table_name, deal_name)
   */
  public saveDealForSession(sessionId: string, deal: { deal_id: string; ke_table_name: string; deal_name: string }): void {
    try {
      const expiresAt = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000); // Same expiry as session
      const dealData: StoredDealData = {
        deal_id: deal.deal_id,
        ke_table_name: deal.ke_table_name,
        deal_name: deal.deal_name,
        timestamp: Date.now(),
        expiresAt,
      };

      const sessionDeals = this.getItem<SessionDealMap>(STORAGE_KEYS.SESSION_DEALS) || {};
      sessionDeals[sessionId] = dealData;
      this.setItem(STORAGE_KEYS.SESSION_DEALS, sessionDeals);
      console.log(`[localStorageService] ✅ Deal saved for session "${sessionId}":`, deal.deal_name);
    } catch (error) {
      console.error('[localStorageService] Error saving deal for session:', error);
    }
  }

  /**
   * Get deal preference for a specific session (Finance Cleve)
   * @param sessionId - The session/conversation ID
   * @returns The stored deal or null if not found/expired
   */
  public getDealForSession(sessionId: string): { deal_id: string; ke_table_name: string; deal_name: string } | null {
    try {
      const sessionDeals = this.getItem<SessionDealMap>(STORAGE_KEYS.SESSION_DEALS);
      const dealData = sessionDeals?.[sessionId];

      if (!dealData) {
        return null;
      }

      // Check if deal data has expired
      if (Date.now() > dealData.expiresAt) {
        console.log(`[localStorageService] Deal expired for session "${sessionId}", clearing`);
        this.clearDealForSession(sessionId);
        return null;
      }

      return { deal_id: dealData.deal_id, ke_table_name: dealData.ke_table_name, deal_name: dealData.deal_name };
    } catch (error) {
      console.error('[localStorageService] Error getting deal for session:', error);
      return null;
    }
  }

  /**
   * Clear deal preference for a specific session (Finance Cleve)
   * @param sessionId - The session/conversation ID
   */
  public clearDealForSession(sessionId: string): void {
    try {
      const sessionDeals = this.getItem<SessionDealMap>(STORAGE_KEYS.SESSION_DEALS);

      if (sessionDeals && sessionDeals[sessionId]) {
        delete sessionDeals[sessionId];
        this.setItem(STORAGE_KEYS.SESSION_DEALS, sessionDeals);
        console.log(`[localStorageService] ✅ Deal cleared for session "${sessionId}"`);
      }
    } catch (error) {
      console.error('[localStorageService] Error clearing deal for session:', error);
    }
  }

  /**
   * Clear all brand preferences
   */
  public clearAllBrands(): void {
    try {
      this.removeItem(STORAGE_KEYS.SESSION_BRANDS);
      console.log('[localStorageService] All brand preferences cleared');
    } catch (error) {
      console.error('[localStorageService] Error clearing all brands:', error);
    }
  }

  // ============================================
  // Pre-Validation Tracking Methods (NiceCxOne)
  // ============================================

  /**
   * Save pre-validation completion status for a state
   * This prevents pre-validation from running more than once per iframe session
   * @param state - The client state ID from NiceCxOne
   * @param messageContent - The message that was sent (for debugging)
   */
  public savePreValidationCompleted(state: string, messageContent: string): void {
    try {
      const expiresAt = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000); // Same expiry as session
      const preValidationData: PreValidationData = {
        state,
        completedAt: Date.now(),
        messageContent,
        expiresAt,
      };

      const preValidationStates = this.getItem<PreValidationStateMap>(STORAGE_KEYS.PREVALIDATION_STATES) || {};
      preValidationStates[state] = preValidationData;
      this.setItem(STORAGE_KEYS.PREVALIDATION_STATES, preValidationStates);
      console.log(`[localStorageService] ✅ Pre-validation marked complete for state "${state}"`);
    } catch (error) {
      console.error('[localStorageService] Error saving pre-validation status:', error);
    }
  }

  /**
   * Check if pre-validation has already been completed for a state
   * @param state - The client state ID from NiceCxOne
   * @returns true if pre-validation has already run for this state
   */
  public hasPreValidationCompleted(state: string): boolean {
    try {
      const preValidationStates = this.getItem<PreValidationStateMap>(STORAGE_KEYS.PREVALIDATION_STATES);
      const preValidationData = preValidationStates?.[state];

      if (!preValidationData) {
        console.log(`[localStorageService] No pre-validation record for state "${state}"`);
        return false;
      }

      // Check if data has expired
      if (Date.now() > preValidationData.expiresAt) {
        console.log(`[localStorageService] Pre-validation record expired for state "${state}", clearing`);
        this.clearPreValidationForState(state);
        return false;
      }

      console.log(`[localStorageService] ✅ Pre-validation already completed for state "${state}" at ${new Date(preValidationData.completedAt).toISOString()}`);
      return true;
    } catch (error) {
      console.error('[localStorageService] Error checking pre-validation status:', error);
      return false;
    }
  }

  /**
   * Clear pre-validation status for a specific state
   * @param state - The client state ID from NiceCxOne
   */
  public clearPreValidationForState(state: string): void {
    try {
      const preValidationStates = this.getItem<PreValidationStateMap>(STORAGE_KEYS.PREVALIDATION_STATES);

      if (preValidationStates && preValidationStates[state]) {
        delete preValidationStates[state];
        this.setItem(STORAGE_KEYS.PREVALIDATION_STATES, preValidationStates);
        console.log(`[localStorageService] ✅ Pre-validation status cleared for state "${state}"`);
      }
    } catch (error) {
      console.error('[localStorageService] Error clearing pre-validation status:', error);
    }
  }

  /**
   * Clear all pre-validation states
   */
  public clearAllPreValidationStates(): void {
    try {
      this.removeItem(STORAGE_KEYS.PREVALIDATION_STATES);
      console.log('[localStorageService] All pre-validation states cleared');
    } catch (error) {
      console.error('[localStorageService] Error clearing all pre-validation states:', error);
    }
  }

  /**
   * Clear all stored data
   */
  public clearAll(): void {
    this.clearAuthData();
    this.clearSessionData(); // Clear normal mode session
    this.clearAllClientStateBots(); // Clear iframe mode state bot preferences
    this.clearAllBrands(); // Clear brand preferences
    this.clearAllPreValidationStates(); // Clear pre-validation tracking
    this.clearLastBotId();
    this.clearCurrentAgent(); // Clear current agent selection
    console.log('[localStorageService] All localStorage data cleared');
  }

  /**
   * Check if auth token is valid (exists and not expired)
   */
  public isAuthValid(): boolean {
    const authData = this.getAuthData();
    return authData !== null;
  }

  /**
   * Get remaining time until token expiry in milliseconds
   */
  public getTokenExpiryTime(): number {
    const authData = this.getAuthData();
    if (!authData) {
      return 0;
    }
    return Math.max(0, authData.expiresAt - Date.now());
  }

  /**
   * Get the auth token directly (convenience method)
   */
  public getToken(): string | null {
    const authData = this.getAuthData();
    return authData ? authData.token : null;
  }

  /**
   * Get the user ID directly (convenience method)
   */
  public getUserId(): string | null {
    const authData = this.getAuthData();
    return authData ? authData.userId : null;
  }

  /**
   * Get the user groups directly (convenience method)
   */
  public getUserGroups(): string[] {
    const authData = this.getAuthData();
    return authData ? authData.userGroups : [];
  }
}

// Export singleton instance
export const localStorageService = LocalStorageService.getInstance();

